import { useLayoutEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { monthToReveal } from "@/lib/mobile-defaults";
import { MONTHS_FR, usePeriod, type Period } from "@/lib/period";

export function PeriodSelector() {
  const { period, setPeriod } = usePeriod();

  // Année de référence affichée : celle de la période, sinon l'année courante.
  const refYear =
    period.kind === "all" ? new Date().getFullYear() : period.year;

  const selectedMonth = period.kind === "month" ? period.month : null;

  // Sur téléphone la bande défile : on centre le mois utile (sélectionné, sinon
  // le mois courant). scrollLeft du conteneur seulement, pas scrollIntoView
  // (qui ferait aussi défiler la page).
  const stripRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // Téléphone seulement : au-dessus de sm la bande ne défile pas.
    if (!window.matchMedia("(max-width: 639px)").matches) return;
    const strip = stripRef.current;
    const btn = strip?.children[monthToReveal(period, new Date())] as
      | HTMLElement
      | undefined;
    if (!strip || !btn) return;
    strip.scrollLeft = btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2;
  }, [period]);

  function setYear(delta: number) {
    if (period.kind === "all") {
      // En mode "Tout", revenir d'abord sur l'année courante en vue annuelle.
      setPeriod({ kind: "year", year: new Date().getFullYear() + delta });
      return;
    }
    setPeriod({ ...period, year: period.year + delta } as Period);
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 surface px-3 py-2">
      <div className="flex items-center gap-2 font-semibold tabular">
        <button
          type="button"
          className="btn btn-ghost btn-sm px-1.5"
          aria-label="Année précédente"
          onClick={() => setYear(-1)}
        >
          <ChevronLeft size={16} />
        </button>
        <span className="w-12 text-center">{refYear}</span>
        <button
          type="button"
          className="btn btn-ghost btn-sm px-1.5"
          aria-label="Année suivante"
          onClick={() => setYear(1)}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div
        ref={stripRef}
        className="relative flex gap-1.5 overflow-x-auto flex-1 min-w-0 max-sm:order-last max-sm:basis-full"
      >
        {MONTHS_FR.map((label, m) => {
          const active = selectedMonth === m && period.kind === "month";
          return (
            <button
              key={label}
              type="button"
              onClick={() =>
                setPeriod({ kind: "month", year: refYear, month: m })
              }
              aria-pressed={active}
              className={cn(
                "btn btn-sm whitespace-nowrap",
                active ? "btn-primary" : "btn-outline",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--color-border-strong)] p-0.5 bg-[var(--color-surface)]">
        <button
          type="button"
          onClick={() => setPeriod({ kind: "year", year: refYear })}
          aria-pressed={period.kind === "year"}
          className={cn(
            "btn btn-ghost btn-sm",
            period.kind === "year" && "bg-[var(--color-muted)]",
          )}
        >
          Année
        </button>
        <button
          type="button"
          onClick={() => setPeriod({ kind: "all" })}
          aria-pressed={period.kind === "all"}
          className={cn(
            "btn btn-ghost btn-sm",
            period.kind === "all" && "bg-[var(--color-muted)]",
          )}
        >
          Tout
        </button>
      </div>
    </div>
  );
}
